'use client';

import { useEffect, useState } from 'react';
import type Marquee from 'react-fast-marquee';
import { usePrefersReducedMotion } from '@/hooks';

// Lazy load Marquee only when needed
let MarqueeComponent: typeof Marquee | null = null;

interface DynamicMarqueeProps {
  children: React.ReactNode;
  speed?: number;
  gradient?: boolean;
  gradientColor?: string;
  gradientWidth?: number | string;
  pauseOnHover?: boolean;
  pauseOnClick?: boolean;
  direction?: 'left' | 'right';
  play?: boolean;
  loop?: number;
  delay?: number;
  className?: string;
  style?: React.CSSProperties;
  autoFill?: boolean;
}

export default function DynamicMarquee(props: DynamicMarqueeProps) {
  const [Component, setComponent] = useState<typeof Marquee | null>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    // Use requestIdleCallback to defer loading until browser is idle
    const idleCallback =
      'requestIdleCallback' in window
        ? window.requestIdleCallback
        : (cb: IdleRequestCallback) => setTimeout(cb, 1);

    const handle = idleCallback(() => {
      setShouldLoad(true);
    });

    return () => {
      if ('cancelIdleCallback' in window && typeof handle === 'number') {
        window.cancelIdleCallback(handle);
      }
    };
  }, []);

  useEffect(() => {
    if (!shouldLoad) {
      return;
    }

    let cancelled = false;

    if (!MarqueeComponent) {
      import('react-fast-marquee').then((mod) => {
        MarqueeComponent = mod.default;
        if (!cancelled) {
          setComponent(() => mod.default);
        }
      });
    } else {
      // Already cached: resolve on a microtask so the setState happens in a
      // callback rather than synchronously in the effect body.
      const cached = MarqueeComponent;
      Promise.resolve().then(() => {
        if (!cancelled) {
          setComponent(() => cached);
        }
      });
    }

    return () => {
      cancelled = true;
    };
  }, [shouldLoad]);

  if (!Component) {
    // Render a static version while loading
    return (
      <div
        className={props.className}
        style={{ ...props.style, overflow: 'hidden' }}
      >
        <div style={{ display: 'flex', gap: '1rem' }}>{props.children}</div>
      </div>
    );
  }

  // WCAG 2.2.2 / reduced motion: freeze the marquee for users who asked their
  // OS to reduce motion, and pause on hover so mouse users can stop it too.
  return (
    <Component
      {...props}
      play={prefersReducedMotion ? false : (props.play ?? true)}
      pauseOnHover={props.pauseOnHover ?? true}
    />
  );
}
